/* eslint-disable no-restricted-globals */
import React, { useState, useEffect, useContext } from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import moment from 'moment-timezone';
import {
    Dialog, //
    DialogTitle,
    DialogContent,
    Box,
    Typography,
    Input,
    TextField,
    IconButton,
    FormControl,
    FormControlLabel,
    Select,
    MenuItem,
    Checkbox,
    Button,
    debounce,
    Stack,
    Alert,
    AlertTitle,
} from '@mui/material';
import CloseIcon from '@material-ui/icons/Close';
import SearchableSelectAll from '../../../../custom-widgets/SearchableSelectAll';
import SchedulingContext from '../../SchedulingContext';
import Region from '../../../../constants/region';
import { getId, getUserRegion } from '../../../../../utils';
import { getPastDateOffset } from '../../utils';
import { LocalizationConsumer } from '../../../../localization/LocalizationContext';
import { inspectRouteSessionSlug, MILK_CATEGORY } from '../../../../constants';
import RecurringItemFields from './RecurringItem/RecurringItemFields';
import { DatePicker, TimePicker } from '@mui/x-date-pickers-pro';
import LocalizedDatePicker from '../../../../custom-widgets/LocalizedDatePicker';
import ControllerErrorAlert from '../../../forms/controller/ControllerErrorAlert';
import useRecurringItemContext from './RecurringItem/useRecurringItemContext';
import ProducerPickupSequence from '../../../../custom-widgets/ProducerPickupSequence';

const UNCLAIMED = '0';
const END_DATE_DAY_OFFSET = 3;

const dialogSx = {
    outerGrid: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        columnGap: '5px',
    },
    innerGrid: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        columnGap: '5px',
    },
    buttonGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        columnGap: '10px',
    },
    item: {
        padding: '10px 0',
    },
    notes: {
        gridColumn: '1 / span 2',
        padding: (theme) => theme.spacing(1),
    },
    autocomplete: {
        paddingTop: '4px',
        width: '18em',
    },
    checkBox: {
        padding: '0 5px',
        display: 'flex',
        alignItems: 'center',
    },
    formControl: {
        margin: (theme) => theme.spacing(1),
        minWidth: (theme) => theme.spacing(30),
        padding: '0px 0px',
    },
    noTopMargin: {
        marginTop: '0px',
    },
    list: {
        maxHeight: '100%',
        overflow: 'scroll',
    },
    selectPaperProps: {
        style: {
            maxHeight: 250,
            width: 355,
        },
    },
};

function MilkSupplyItemDialogButtonGroup(props) {
    return (
        <Box sx={dialogSx.buttonGrid}>
            <Box sx={dialogSx.item}>
                {props.item.route_session && (
                    <Button onClick={props.handleNavigateToRouteSession} variant="outlined" fullWidth>
                        Inspect Route Session
                    </Button>
                )}
            </Box>
            <Box sx={dialogSx.item}>
                <Button onClick={props.handleClose} variant="contained" color="secondary" fullWidth>
                    Cancel
                </Button>
            </Box>
            <Box sx={dialogSx.item}>
                <Button disabled={(!props.item.routeId && !props.bulkEdit) || props.submitBlockedByRecurringWarning || props.tooManyItemsError || props.error} onClick={props.handleOnClick} variant="contained" color="primary" fullWidth>
                    {!props.isEdit ? 'Create' : 'Edit'}
                </Button>
            </Box>
            <Box></Box>
        </Box>
    );
}

function DateErrorAlert({ dateError }) {
    if (!dateError.start && !dateError.end) return null;
    return <ControllerErrorAlert errors={[dateError.start, dateError.end].filter((item) => item != null)} />;
}

function MilkSupplyItemDialog({ open, date, data, actions, selectedItem, processor, bulkEdit, selectedItemIdsMap, history, restricted }) {
    const { properties } = useContext(SchedulingContext);
    const { isRecurringItemWarningBlockingCreate, setIsRecurringItemWarningBlockingCreate, recurringAmount, tooManyItemsError } = useRecurringItemContext();
    const { routes, routeMap, producers, producerMap, processors, processorMap, haulingCompanies, haulingMap } = data;

    const getDefaultOccurrences = () => {
        if (!properties.recurringItemDefaults.useOccurrencesInsteadOfEndDate)
            return {
                recurEndDate: moment(),
                numberOccurrences: null,
            };

        return {
            recurEndDate: null,
            numberOccurrences: properties.recurringItemDefaults.defaultOccurrences,
        };
    };

    const initialState = {
        id: null,
        routeId: null,
        amount: null,
        producers: [],
        producerIds: null,
        producerPickupSequence: null,
        startDate: null,
        startTime: null,
        processors: [],
        processorId: null,
        selectedProducers: [],
        endDate: null,
        endTime: null,
        haulingCompanies: [],
        haulingId: null,
        dairyType: 'milk',
        deliveryHauler: null,
        ltNumber: null,
        fairlifeNumber: null,
        batch: null,
        po_number: null,
        milk_category: null,
        protein: null,
        route_session: null,
        recurring: false,
        recur_metadata: null,
        recurDayOfMonth: 0,
        days: [date.day()],
        frequency: 1,
        unit: 'day',
        customFrequency: 1,
        notes: '',
        poNumberParams: {
            start_number: null,
            prefix: null,
            suffix: null,
            padding_zeros: 0,
            amount: 1,
        },
        ...getDefaultOccurrences(),
    };

    const [dateError, setDateError] = useState({ start: null, end: null });
    const [producerError, setProducerError] = useState(null);
    const [item, setItem] = useState(initialState);
    const [isEdit, setIsEdit] = useState(false);
    const [includeAllProcessors, setIncludeAllProcessors] = useState(false);
    const [includeAllHaulingCompanies, setIncludeAllHaulingCompanies] = useState(false);
    const [includeDeliveryHauler, setIncludeDeliveryHauler] = useState(false);
    const [availableRoutes] = useState(!!processor ? routes.filter((route) => route.processors.includes(processor.id)) : routes);
    const [disabled, setDisabled] = useState(true);
    const [submitBlockedByRecurringWarning, setSubmitBlockedByRecurringWarning] = useState(false);

    const autoAdjustStartDate = [Region.MMPA].includes(getUserRegion());

    useEffect(() => {
        if (!!selectedItem) {
            setIsEdit(true);
            setIncludeDeliveryHauler(!!selectedItem.deliveryHauler);

            const route = routeMap[selectedItem.route.id];
            if (route) {
                setIncludeAllProcessors((selectedItem.processor && !route.processors.includes(selectedItem.processor.id)) || route.processors.length === 0 || route.processors.length === processors.length - 1); // -1 for unclaimed
                setIncludeAllHaulingCompanies((selectedItem.hauling && !route.hauling_companies.includes(selectedItem.hauling.id)) || route.hauling_companies.length === 0 || route.hauling_companies.length === haulingCompanies.length);
            }

            setItem({
                ...item,
                id: selectedItem.id,
                routeId: route ? route.id : null,
                amount: selectedItem.amount,
                producers,
                producerIds: selectedItem.producerPickupSequence?.map(({ producer_id }) => producer_id),
                producerPickupSequence:
                    selectedItem.producerPickupSequence
                        ?.filter(({ producer_id }) => producerMap[producer_id])
                        .map(({ producer_id, expected_amount }) => ({
                            id: producer_id,
                            name: producerMap[producer_id].name,
                            amount: expected_amount,
                            // wrap
                        })) ?? [],
                selectedProducers: selectedItem.producers.filter((p) => !!p),
                processors: route ? route.processors.filter((p) => !!p).map((id) => processorMap[id]) : [],
                ...(!!selectedItem.processor && { processorId: selectedItem.processor.id }),
                haulingCompanies: route ? route.hauling_companies.filter((h) => !!h).map((id) => haulingMap[id]) : [],
                ...(!!selectedItem.hauling && { haulingId: selectedItem.hauling.id }),
                ...(!!selectedItem.deliveryHauler && { deliveryHauler: selectedItem.deliveryHauler.id }),
                startDate: selectedItem.startDate,
                endDate: selectedItem.endDate,
                dairyType: selectedItem.dairyType,
                recurring: selectedItem.recurring,
                recur_metadata: selectedItem.recur_metadata,
                ltNumber: selectedItem.ltNumber,
                fairlifeNumber: selectedItem.fairlifeNumber,
                batch: selectedItem.batch,
                po_number: selectedItem.poNumber,
                milk_category: route?.category,
                protein: selectedItem.protein ? selectedItem.protein.toString() : null,
                route_session: selectedItem.route_session ? selectedItem.route_session : null,
                notes: selectedItem.notes,
                milk_classification: selectedItem.milk_classification,
            });

            setDisabled(false);
        }
        if (bulkEdit) {
            setIsEdit(true);
            setDisabled(false);
            setItem({
                ...item,
                producers,
            });
            setIncludeAllProcessors(true);
            setIncludeAllHaulingCompanies(true);
        }
        if (processor) {
            setItem({
                ...item,
                processorId: processor.id,
                processors: [processor],
            });
        }
    }, []);

    useEffect(() => {
        if (isRecurringItemWarningBlockingCreate) {
            setSubmitBlockedByRecurringWarning(true);
        } else {
            setSubmitBlockedByRecurringWarning(false);
        }

        return () => {
            setSubmitBlockedByRecurringWarning(false);
        };
    }, [isRecurringItemWarningBlockingCreate]);

    const processorLogic = (value) => {
        return !processor && { processors: value.processors.filter((p) => !!p).map((id) => processorMap[id]), ...(value.processors.length > 0 && { processorId: value.processors[0] }) };
    };

    const handleRouteChange = (_event, value) => {
        const rId = value?.id;
        if (!bulkEdit && rId) {
            setIncludeAllProcessors(value.processors.length === 0 || value.processors.length === processors.length - 1); // -1 for unclaimed
            setIncludeAllHaulingCompanies(value.hauling_companies.length === haulingCompanies.length);
            setItem({
                ...item,
                routeId: rId,
                amount: value.amount,
                producers,
                producerIds: value.users,
                selectedProducers: value.users.map((id) => producerMap[id]),
                producerPickupSequence: value.producer_pickup_sequence?.map(({ producer_id, expected_amount }) => ({
                    id: producer_id,
                    name: producerMap[producer_id].name,
                    amount: expected_amount,
                })),
                ...processorLogic(value),
                haulingCompanies: restricted ? [haulingMap[getId()]] : value.hauling_companies.map((id) => haulingMap[id]),
                ...(restricted ? { haulingId: getId() } : value.hauling_companies.length > 0 && { haulingId: value.hauling_companies[0] }),
                startDate: moment(`${date.format('YYYY-MM-DD')} ${value.start_time}`, 'YYYY-MM-DD HH:mm:ss'),
                endDate: moment(`${date.format('YYYY-MM-DD')} ${value.end_time}`, 'YYYY-MM-DD HH:mm:ss'),
                dairyType: value.type_of_fluid,
                ...(value.protein && { protein: value.protein.toString() }),
                milk_category: value?.category,
                notes: value.notes,
                recurEndDate: moment(`${date.format('YYYY-MM-DD')} ${value.start_time}`, 'YYYY-MM-DD HH:mm:ss'),
            });
            setDisabled(false);
        } else {
            setItem({
                ...item,
                routeId: rId || null,
            });
        }
    };

    /*
     *  Ideally a validator would be added to the form but that would involve a large rewrite of the
     *  way the schedule works.
     */
    const areDatesValid = () => {
        const minDate = moment().subtract(getPastDateOffset(), 'day').startOf('day');
        const { startDate, endDate } = item;
        const maxDate = moment(startDate).add(END_DATE_DAY_OFFSET, 'days').endOf('day');

        // If the start date is before one day before the current date error
        if (startDate.isBefore(minDate)) {
            setDateError({ ...dateError, start: 'Start date is before minimum date.' });
            return false;
        }

        // If the end date is more than 3 days after the start date, set an error message
        if (endDate.isAfter(maxDate)) {
            setDateError({ ...dateError, end: `End date is more than ${END_DATE_DAY_OFFSET} days after start date.` });
            return false;
        }

        // If the end date is before the start date error
        if (endDate.isBefore(startDate)) {
            // For certain regions, start date will be automatically corrected under transformItem
            if (autoAdjustStartDate) {
                return true;
            }

            setDateError({ ...dateError, end: 'End date is before start date.' });
            return false;
        }

        return true;
    };

    const handleValueChange = (event) => {
        const { name, value } = event.target;

        setItem({
            ...item,
            [name]: value,
        });
    };

    const handleAmountChange = (event) => {
        const value = parseInt(event.target.value);
        setItem({
            ...item,
            amount: !isNaN(value) && value >= 0 ? value : 0,
        });
    };

    const handleProducersChange = (event, value) => {
        const selected = Array.isArray(value) ? value : [];
        if (selected.length < 1) {
            setProducerError('Must select at least one producer');
        } else {
            setProducerError(null);
        }
        setItem({
            ...item,
            producerIds: selected.map((v) => v.id),
            producerPickupSequence: selected.map(({ id }) => ({ id, name: producerMap[id]?.name ?? '', amount: (Array.isArray(item.producerPickupSequence) ? item.producerPickupSequence : []).find((s) => s.id === id)?.amount ?? 0 })),
            selectedProducers: selected,
        });
    };

    const handleStartDateChange = (d, c) => {
        if (c?.validationError === 'invalidDate') {
            setItem(() => ({ ...item, startDate: moment().add(1, 'minutes') }));
            return;
        }
        const itemClone = { ...item };

        // no additional validation needed for bulk edit
        if (bulkEdit) {
            itemClone.startDate = d;
        } else if (d.isValid()) {
            const minDate = selectedItem?.startDate?.startOf('day').subtract(1, 'day') ?? moment().startOf('day').subtract(1, 'day');

            if (d.isBefore(minDate)) {
                itemClone.startDate = moment().add(1, 'minutes');
            } else itemClone.startDate = d;

            const endBeforeStart = itemClone.endDate && itemClone.endDate.isBefore(itemClone.startDate);
            const maxDate = itemClone.startDate.clone().add(END_DATE_DAY_OFFSET, 'days').endOf('day');
            if (itemClone.endDate == null || endBeforeStart) {
                itemClone.endDate = itemClone.startDate.clone();
            } else if (itemClone.endDate.isAfter(maxDate)) {
                itemClone.endDate = itemClone.endDate.clone().set({
                    year: maxDate.year(),
                    month: maxDate.month(),
                    date: maxDate.date(),
                });
            }
        }
        setItem(() => itemClone);
    };

    const handleProcessorChange = (event, value) => {
        if (bulkEdit) {
            if (value.name === 'All') {
                setIncludeAllProcessors(true);
                setItem({
                    ...item,
                    processorId: null,
                });
            } else if (value.name === 'Unclaimed') {
                setItem({
                    ...item,
                    processorId: '0',
                });
            } else {
                setItem({
                    ...item,
                    processorId: value.id,
                });
            }
        } else if (event.target.value === 'all') {
            setIncludeAllProcessors(true);
        } else {
            setItem({
                ...item,
                processorId: event.target.value,
            });
        }
    };

    const handleEndDateChange = (d) => {
        const itemClone = { ...item };

        // no additional validation needed for bulk edit
        if (bulkEdit) {
            itemClone.endDate = d;
        } else if (d.isValid()) {
            const maxDate = itemClone.startDate.clone().add(END_DATE_DAY_OFFSET, 'days').endOf('day');

            if (d.isAfter(maxDate)) {
                itemClone.endDate = maxDate;
            } else {
                itemClone.endDate = d;
            }

            const endBeforeStart = itemClone.endDate.isBefore(item.startDate);
            if (endBeforeStart) {
                itemClone.startDate = itemClone.endDate.clone();
            }
        }
        if (itemClone.endDate.isSame(item.endDate)) {
            /*  
                This is a work around to the state not triggering field rerender when the date is 
                the same. Tried to use a force state variable as a separate useState and directly in  
                the object but it didn't work. Also tried to use a key on the date picker but that 
                didn't work either.
            */
            setItem((i) => ({ ...i, endDate: itemClone.endDate.clone().add(1, 'minutes'), startDate: itemClone.startDate }));
        }
        setItem((i) => ({ ...i, endDate: itemClone.endDate, startDate: itemClone.startDate }));
    };

    const handleHaulingCompanyChange = (event, value) => {
        if (bulkEdit) {
            if (value.name === 'All') {
                setIncludeAllHaulingCompanies(true);
                setItem({
                    ...item,
                    haulingId: null,
                });
            } else if (value.name === 'Unclaimed') {
                setItem({
                    ...item,
                    haulingId: '0',
                });
            } else {
                setItem({
                    ...item,
                    haulingId: value.id,
                });
            }
        } else if (event.target.value === 'all') {
            setIncludeAllHaulingCompanies(true);
        } else {
            setItem({
                ...item,
                haulingId: event.target.value,
            });
        }
    };

    const handleStartTimeChange = (t) => {
        setItem({
            ...item,
            startTime: t,
        });
    };

    const handleEndTimeChange = (t) => {
        setItem({
            ...item,
            endTime: t,
        });
    };

    const handleBatchChange = (event) => {
        const value = parseInt(event.target.value);
        setItem({
            ...item,
            batch: !isNaN(value) ? value : 0,
        });
    };

    const handleProteinChange = (event) => {
        const { value } = event.target;

        setItem({
            ...item,
            protein: !value || (!isNaN(value) && !isNaN(parseFloat(value))) ? value : item.protein,
        });
    };

    const handleRecurringChange = (event) => {
        setItem({
            ...item,
            recurring: event.target.checked,
        });
    };

    const handleClassificationChange = (event) => {
        setItem({
            ...item,
            milk_classification: event.target.value,
        });
    };

    const handleToggleDeliveryHauler = (event) => {
        const { checked } = event.target;

        setIncludeDeliveryHauler(checked);

        if (!checked) {
            setItem({
                ...item,
                deliveryHauler: null,
            });
        }
    };

    const handleDraggableListChange = (selected) => {
        const producerPickupSequence = selected.map(({ id, amount }) => ({ id, amount: Number(amount || 0) }));
        setItem({ ...item, producerIds: selected.map(({ id }) => id), selectedProducers: selected.map(({ id }) => producerMap[id]), producerPickupSequence });
    };

    const handleClose = () => {
        setSubmitBlockedByRecurringWarning(false);
        setIsRecurringItemWarningBlockingCreate(false);
        actions.handleCloseItemDialog('milkSupply');
        setItem(initialState);
    };

    const transformItem = () => {
        const itemClone = { ...item };

        if (itemClone.protein) {
            itemClone.protein = parseFloat(itemClone.protein);
        }

        if (
            autoAdjustStartDate &&
            itemClone.startDate &&
            itemClone.endDate &&
            itemClone.startDate.isAfter(itemClone.endDate)
            // For certain regions, start date is automatically corrected if after end date
        ) {
            itemClone.startDate = itemClone.endDate.clone();
        }

        if (itemClone.producerPickupSequence) {
            itemClone.producerIds = itemClone.producerPickupSequence.map(({ id }) => id);
            itemClone.producerPickupSequence = itemClone.producerPickupSequence.map(({ id, amount }) => ({ producer_id: id, expected_amount: amount }));
        }

        return itemClone;
    };

    const handleNavigateToRouteSession = () => {
        const routeSessionId = item.route_session._id ? item.route_session._id : item.route_session.id; // InspectRouteSessionApp expects an 'id' property rather than '_id'
        history.push({ pathname: `${inspectRouteSessionSlug}/${routeSessionId}` });
    };

    const handleCreate = () => {
        if (!areDatesValid()) return;
        if (!item.producerIds?.length) {
            setProducerError('Must select at least one producer');
            return;
        }
        actions.handleItemCreate(transformItem());

        handleClose();
    };

    const handleEdit = (recur) => {
        if (!areDatesValid()) return;
        if (!item.producerIds?.length) {
            setProducerError('Must select at least one producer');
            return;
        }
        actions.handleItemEdit(transformItem(), recur);
        actions.handleCloseItemDialog('milkSupply');
        setItem(initialState);
    };

    const handleBulkEdit = () => {
        const { startDate, endDate } = item;
        // Only run validation when startDate and endDate are set
        if (startDate && endDate && !areDatesValid()) return;
        // producerIds is null when untouched, [] when user explicitly cleared
        if (Array.isArray(item.producerIds) && item.producerIds.length === 0) {
            setProducerError('Must select at least one producer');
            return;
        }
        actions.handleBulkItemEdit(transformItem());
        actions.handleCloseItemDialog('milkSupply');
        setItem(initialState);
    };

    const handleOnClick = () => {
        if (!isEdit) {
            handleCreate();
        } else if (!bulkEdit) {
            actions.handleConfirmDialogOpen({
                action: () => handleEdit(false), // Wrap
                title: 'Edit Scheduling Item?',
                text: 'Are you sure you want to edit this schedule item?',
                ...(item.recurring && { secondaryAction: () => handleEdit(true), secondaryText: 'Edit Recurring?' }),
            });
        } else {
            actions.handleConfirmDialogOpen({
                action: () => handleBulkEdit(), // Wrap
                title: 'Edit Scheduling Items?',
                text: `Are you sure you want to edit ${Object.keys(selectedItemIdsMap).length === 1 ? 'this schedule item?' : `these ${Object.keys(selectedItemIdsMap).length} schedule items?`}`,
            });
        }
    };

    const DialogBoxTitle = (
        <DialogTitle>
            <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h5">Details</Typography>
                <IconButton aria-label="close" onClick={handleClose}>
                    <CloseIcon />
                </IconButton>
            </Box>
        </DialogTitle>
    );

    return (
        <LocalizationConsumer>
            {(localization) => (
                <Dialog open={open} onClose={actions.handleCloseDialog} maxWidth="lg">
                    {DialogBoxTitle}
                    <DialogContent>
                        <Box sx={dialogSx.outerGrid}>
                            <Box sx={dialogSx.innerGrid}>
                                <FormControl sx={dialogSx.autocomplete}>
                                    <Box>Route</Box>
                                    <SearchableSelectAll variant="outlined" style={{ maxWidth: 450, maxLines: 1 }} options={availableRoutes} onChange={handleRouteChange} customGroup={(option) => MILK_CATEGORY[option.category]} value={item.routeId ? routeMap[item.routeId] : ''} />
                                </FormControl>
                                <Box sx={dialogSx.item}>
                                    <FormControl sx={dialogSx.formControl}>
                                        <Box sx={{ color: disabled ? 'grey' : 'black' }}>Expected Amount</Box>
                                        <TextField variant="outlined" disabled={disabled} value={item.amount} onChange={handleAmountChange} />
                                    </FormControl>
                                </Box>
                                <Box sx={dialogSx.item}>
                                    <FormControl sx={dialogSx.autocomplete}>
                                        <Box sx={{ color: disabled ? 'grey' : 'black' }}>Producers</Box>
                                        <SearchableSelectAll variant="outlined" style={{ maxWidth: 450, maxLines: 1 }} multiple value={item.selectedProducers.filter((producer) => producer?.name)} onChange={handleProducersChange} options={producers} disabled={disabled} disableListWrap disablePortal />
                                        <Typography variant="caption" color="red" sx={{ visibility: !!producerError ? 'visible' : 'hidden' }}>
                                            {producerError}
                                        </Typography>
                                    </FormControl>
                                </Box>
                                <Box sx={dialogSx.item}>
                                    <FormControl sx={dialogSx.formControl}>
                                        <Box sx={{ color: disabled ? 'grey' : 'black' }}>Expected Start Time</Box>
                                        {bulkEdit ? (
                                            <Stack direction="row" spacing={2}>
                                                <LocalizedDatePicker Picker={DatePicker} disabled={disabled} style={dialogSx.noTopMargin} margin="normal" value={item.startDate} minDate={moment()} onChange={handleStartDateChange} />
                                                <LocalizedDatePicker Picker={TimePicker} disabled={disabled} style={dialogSx.noTopMargin} margin="normal" value={item.startTime} onChange={handleStartTimeChange} ampm={!properties.has24HourClock} />
                                            </Stack>
                                        ) : (
                                            <LocalizedDatePicker
                                                disabled={disabled} //
                                                minDate={moment().subtract(getPastDateOffset(), 'day').startOf('day')}
                                                value={item.startDate}
                                                onChange={debounce(handleStartDateChange, 1000)}
                                                ampm={!properties.has24HourClock}
                                            />
                                        )}
                                    </FormControl>
                                </Box>
                                <Box sx={dialogSx.item}>
                                    <FormControl variant="outlined" sx={dialogSx.formControl}>
                                        <Box sx={{ color: disabled ? 'grey' : 'black' }}>{localization.general.processor_singular}</Box>
                                        {bulkEdit ? (
                                            <SearchableSelectAll
                                                variant="outlined"
                                                disabled={disabled || !!processor}
                                                value={processors.find((p) => p.id === item.processorId)}
                                                onChange={handleProcessorChange}
                                                options={[
                                                    ...processors
                                                        .filter((p) => !!p)
                                                        .map((p) => ({
                                                            id: p.id,
                                                            name: p.name,
                                                        })),
                                                ]}
                                            />
                                        ) : (
                                            <Select disabled={disabled || !!processor} value={item.processorId} onChange={handleProcessorChange}>
                                                {bulkEdit && <MenuItem value={null}>None</MenuItem>}
                                                {!includeAllProcessors && <MenuItem value={null}>Unclaimed</MenuItem>}
                                                {!includeAllProcessors &&
                                                    item.processors
                                                        .filter((p) => !!p)
                                                        .map((p) => (
                                                            <MenuItem key={p.id} value={p.id}>
                                                                {p.name}
                                                            </MenuItem>
                                                        ))}
                                                {!includeAllProcessors && <MenuItem value="all">All</MenuItem>}
                                                {includeAllProcessors &&
                                                    processors
                                                        .filter((p) => !!p)
                                                        .map((p) => (!p.id && bulkEdit ? { ...p, id: UNCLAIMED } : p))
                                                        .map((p) => (
                                                            <MenuItem key={p.id} value={p.id}>
                                                                {p.name}
                                                            </MenuItem>
                                                        ))}
                                            </Select>
                                        )}
                                    </FormControl>
                                </Box>
                                <Box sx={dialogSx.item}>
                                    <FormControl sx={dialogSx.formControl}>
                                        <Box sx={{ color: disabled ? 'grey' : 'black' }}>Expected End Time</Box>
                                        {bulkEdit ? (
                                            <Stack direction="row" spacing={2}>
                                                <LocalizedDatePicker Picker={DatePicker} disabled={disabled} style={dialogSx.noTopMargin} margin="normal" value={item.endDate} minDate={moment()} onChange={handleEndDateChange} />
                                                <LocalizedDatePicker Picker={TimePicker} disabled={disabled} style={dialogSx.noTopMargin} margin="normal" value={item.endTime} onChange={handleEndTimeChange} ampm={!properties.has24HourClock} />
                                            </Stack>
                                        ) : (
                                            <LocalizedDatePicker
                                                disabled={disabled} //
                                                value={item.endDate}
                                                minDate={item.startDate?.clone().startOf('day')}
                                                maxDate={item.startDate?.clone().add(END_DATE_DAY_OFFSET, 'days').endOf('day')}
                                                onChange={debounce(handleEndDateChange, 1000)}
                                                ampm={!properties.has24HourClock}
                                            />
                                        )}
                                    </FormControl>
                                </Box>
                                <Box sx={dialogSx.item}>
                                    <FormControl variant="outlined" sx={dialogSx.formControl}>
                                        <Box sx={{ color: disabled ? 'grey' : 'black' }}>{localization.general.hauling_singular}</Box>
                                        {bulkEdit ? (
                                            <SearchableSelectAll
                                                variant="outlined"
                                                disabled={disabled || restricted}
                                                value={haulingCompanies.find((h) => h.id === item.haulingId)}
                                                onChange={handleHaulingCompanyChange}
                                                options={[
                                                    { id: null, name: 'None' },
                                                    ...haulingCompanies
                                                        .filter((p) => !!p)
                                                        .map((p) => ({
                                                            id: p.id,
                                                            name: p.name,
                                                        })),
                                                ]}
                                            />
                                        ) : (
                                            <Select disabled={disabled || restricted} value={item.haulingId} onChange={handleHaulingCompanyChange}>
                                                <MenuItem value={null}>None</MenuItem>
                                                {!includeAllHaulingCompanies &&
                                                    item.haulingCompanies
                                                        .filter((h) => !!h)
                                                        .map((h) => (
                                                            <MenuItem key={h.id} value={h.id}>
                                                                {h.name}
                                                            </MenuItem>
                                                        ))}

                                                {!includeAllHaulingCompanies && <MenuItem value="all">All</MenuItem>}
                                                {includeAllHaulingCompanies &&
                                                    haulingCompanies
                                                        .map((h) => (!h.id && bulkEdit ? { ...h, id: UNCLAIMED } : h))
                                                        .map((h) => (
                                                            <MenuItem key={h.id} value={h.id}>
                                                                {h.name}
                                                            </MenuItem>
                                                        ))}
                                            </Select>
                                        )}
                                    </FormControl>
                                </Box>
                                {properties.hasBatch && (
                                    <Box sx={dialogSx.item}>
                                        <FormControl sx={dialogSx.formControl}>
                                            <Box sx={{ color: disabled ? 'grey' : 'black' }}>Batch</Box>
                                            <TextField disabled={disabled} value={item.batch} onChange={handleBatchChange} variant="outlined" />
                                        </FormControl>
                                    </Box>
                                )}
                                {properties.hasPO && (
                                    <Box sx={dialogSx.item}>
                                        <FormControl sx={dialogSx.formControl}>
                                            <Box sx={{ color: disabled ? 'grey' : 'black' }}>{localization.general.customer_po}</Box>
                                            <TextField
                                                name="po_number" // Wrap
                                                disabled={disabled}
                                                value={item.po_number}
                                                onChange={handleValueChange}
                                                variant="outlined"
                                            />
                                        </FormControl>
                                    </Box>
                                )}
                                {properties.hasComponents && (
                                    <Box sx={dialogSx.item}>
                                        <FormControl sx={dialogSx.formControl}>
                                            <Box sx={{ color: disabled ? 'grey' : 'black' }}>Protein</Box>
                                            <Input disabled={disabled} value={item.protein} onChange={handleProteinChange} />
                                        </FormControl>
                                    </Box>
                                )}
                                {properties.hasComponents && <Box />}
                                {isEdit && (
                                    <Box sx={dialogSx.item}>
                                        <FormControl sx={dialogSx.formControl}>
                                            <Box sx={{ color: disabled ? 'grey' : 'black' }}>LT Number</Box>
                                            <Typography>{item.ltNumber}</Typography>
                                        </FormControl>
                                    </Box>
                                )}
                                {properties.hasMilkClassification && (
                                    <Box sx={dialogSx.formControl}>
                                        <Box>Classification</Box>
                                        <Select
                                            value={item.milk_classification}
                                            onChange={handleClassificationChange}
                                            sx={{ minWidth: (theme) => theme.spacing(30) }}
                                            displayEmpty
                                            // wrap
                                        >
                                            <MenuItem value={null}>None</MenuItem>
                                            <MenuItem value="PP">PP</MenuItem>
                                        </Select>
                                    </Box>
                                )}
                                <Box sx={dialogSx.notes}>
                                    Notes
                                    <TextField name="notes" disabled={disabled} value={item.notes} onChange={handleValueChange} variant="outlined" fullWidth multiline inputProps={{ maxLength: 255 }} maxRows={4} />
                                </Box>
                                {!properties.hasDeliveryHauler && <Box />}
                                {properties.hasDeliveryHauler && (
                                    <Box sx={dialogSx.item}>
                                        <Box sx={dialogSx.checkBox}>
                                            <FormControlLabel
                                                disabled={disabled} // Wrap
                                                control={<Checkbox value={includeDeliveryHauler} checked={includeDeliveryHauler} onChange={handleToggleDeliveryHauler} />}
                                                label={`Secondary ${localization.general.hauling_singular}`}
                                            />
                                        </Box>
                                    </Box>
                                )}
                                {includeDeliveryHauler ? <DeliveryHaulerField {...{ localization, item, handleValueChange, haulingCompanies }} /> : <Box />}
                            </Box>
                            <Box>
                                <Typography variant="h6">Pickup Order</Typography>
                                <ProducerPickupSequence fields={item.producerPickupSequence} onChange={handleDraggableListChange} />
                            </Box>
                        </Box>
                        <RecurringItemFields hide={isEdit} item={item} setItem={setItem} disabled={disabled} handleRecurringChange={handleRecurringChange} />
                        <DateErrorAlert dateError={dateError} />
                        {isRecurringItemWarningBlockingCreate ? (
                            <Alert severity="warning" sx={{ marginTop: (theme) => theme.spacing(2) }}>
                                <AlertTitle>Warning: Creating over 100 items</AlertTitle>
                                <Typography variant="body1">Please verify that the item amount and dates are correct in the preview table and accept the creation by checking the box.</Typography> <br />
                                <FormControlLabel
                                    control={<Checkbox checked={!submitBlockedByRecurringWarning} onChange={() => setSubmitBlockedByRecurringWarning(!submitBlockedByRecurringWarning)} />}
                                    label={
                                        <>
                                            I have verified the dates and amount of the <strong>{recurringAmount}</strong> items in the preview table are correct.
                                        </>
                                    }
                                />
                            </Alert>
                        ) : null}
                        <MilkSupplyItemDialogButtonGroup
                            bulkEdit={bulkEdit} // Wrap
                            item={item}
                            error={!!producerError}
                            isEdit={isEdit}
                            dialogSx={dialogSx}
                            handleClose={handleClose}
                            handleNavigateToRouteSession={handleNavigateToRouteSession}
                            handleOnClick={handleOnClick}
                            submitBlockedByRecurringWarning={submitBlockedByRecurringWarning}
                            tooManyItemsError={tooManyItemsError}
                        ></MilkSupplyItemDialogButtonGroup>
                    </DialogContent>
                </Dialog>
            )}
        </LocalizationConsumer>
    );
}

const DeliveryHaulerField = ({ localization, item, handleValueChange, haulingCompanies }) => {
    return (
        <Box sx={dialogSx.item}>
            <FormControl variant="outlined" sx={dialogSx.formControl}>
                <Box>{localization.general.hauling_delivery}</Box>
                <Select name="deliveryHauler" value={item.deliveryHauler} onChange={handleValueChange}>
                    {haulingCompanies.filter(Boolean).map((hauler) => (
                        <MenuItem key={hauler.id || null} value={hauler.id || null}>
                            {hauler.name}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        </Box>
    );
};

function mapDispatchToProps(dispatch) {
    return bindActionCreators({}, dispatch);
}

export default withRouter(connect(null, mapDispatchToProps)(MilkSupplyItemDialog));
